'use client';

import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useAuth } from '@/components/AuthProvider';

// ── Context ───────────────────────────────────────────────────────────────────

interface BugReportContextValue {
  openBugReport: () => void;
}

const BugReportContext = createContext<BugReportContextValue | null>(null);

// ── Icons ─────────────────────────────────────────────────────────────────────

function CheckCircleIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="24" cy="24" r="20" />
      <path d="M14 24L20 30L34 16" />
    </svg>
  );
}

// ── Provider (contains the modal) ─────────────────────────────────────────────

export function BugReportProvider({ children }: { children: ReactNode }) {
  const [open, setOpen]             = useState(false);
  const [whatDoing, setWhatDoing]   = useState('');
  const [whatHappened, setWhatHappened] = useState('');
  const [email, setEmail]           = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess]       = useState(false);
  const [error, setError]           = useState('');
  const closeTimerRef               = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { user } = useAuth();

  function openBugReport() {
    setWhatDoing('');
    setWhatHappened('');
    setEmail(user?.email ?? '');
    setSubmitting(false);
    setSuccess(false);
    setError('');
    setOpen(true);
  }

  function close() {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setOpen(false);
  }

  // Keep email in sync if user loads after modal opens
  useEffect(() => {
    if (open && user?.email && !email) setEmail(user.email);
  }, [open, user?.email, email]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!whatDoing.trim() && !whatHappened.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch('/api/bug-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ whatDoing, whatHappened, email }),
      });
      if (!res.ok) throw new Error('Failed');
      setSuccess(true);
      closeTimerRef.current = setTimeout(() => {
        setOpen(false);
      }, 3000);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <BugReportContext.Provider value={{ openBugReport }}>
      {children}

      {open && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 z-50"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={close}
          />

          {/* Modal card */}
          <div
            className="fixed z-[51] bg-white shadow-xl"
            style={{
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 'calc(100% - 48px)',
              maxWidth: '480px',
              borderRadius: '16px',
              padding: '24px',
            }}
            onClick={e => e.stopPropagation()}
          >
            {success ? (
              /* Success state */
              <div className="flex flex-col items-center text-center py-4 gap-4">
                <span className="text-deep-green">
                  <CheckCircleIcon />
                </span>
                <p className="font-heading text-[20px] text-dark-text leading-snug">
                  Thank you
                </p>
                <p className="font-body text-[14px] text-sage leading-relaxed">
                  We received your report and will look into it.
                </p>
              </div>
            ) : (
              /* Form state */
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <h2 className="font-heading text-[22px] text-dark-text">Report an Issue</h2>
                  <p className="font-body text-[14px] text-sage mt-1">
                    Found something not working right? Let us know.
                  </p>
                </div>

                <div className="space-y-1">
                  <label className="font-body text-[13px] font-semibold text-dark-text block">
                    What were you doing?
                  </label>
                  <textarea
                    value={whatDoing}
                    onChange={e => setWhatDoing(e.target.value)}
                    placeholder="Describe what you were trying to do..."
                    rows={3}
                    className="w-full font-body text-[14px] text-dark-text bg-cream border border-sand
                               rounded-lg px-3.5 py-2.5 focus:outline-none focus:border-sage
                               transition-colors resize-none"
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-body text-[13px] font-semibold text-dark-text block">
                    What happened?
                  </label>
                  <textarea
                    value={whatHappened}
                    onChange={e => setWhatHappened(e.target.value)}
                    placeholder="Describe what went wrong..."
                    rows={3}
                    className="w-full font-body text-[14px] text-dark-text bg-cream border border-sand
                               rounded-lg px-3.5 py-2.5 focus:outline-none focus:border-sage
                               transition-colors resize-none"
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-body text-[13px] font-semibold text-dark-text block">
                    Your email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full font-body text-[14px] text-dark-text bg-cream border border-sand
                               rounded-lg px-3.5 py-2.5 focus:outline-none focus:border-sage
                               transition-colors"
                  />
                </div>

                {error && (
                  <p className="font-body text-[13px] text-alert-red">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting || (!whatDoing.trim() && !whatHappened.trim())}
                  className="w-full bg-deep-green text-white font-body font-semibold text-[15px]
                             py-3 rounded-xl hover:opacity-90 transition-opacity
                             disabled:opacity-40"
                >
                  {submitting ? 'Sending...' : 'Send Report'}
                </button>

                <button
                  type="button"
                  onClick={close}
                  className="w-full font-body text-[13px] text-sage hover:text-dark-text
                             transition-colors text-center py-1"
                >
                  Cancel
                </button>
              </form>
            )}
          </div>
        </>
      )}
    </BugReportContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useBugReport(): BugReportContextValue {
  const ctx = useContext(BugReportContext);
  if (!ctx) throw new Error('useBugReport must be used inside <BugReportProvider>');
  return ctx;
}
